import React, { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { FiArrowLeft, FiEdit2, FiStar } from "react-icons/fi";
import PostAuthor from "../component/PostAuthor";
import Loader from "../component/Loader";
import DeletePost from "./DeletePost";
import { UserContext } from "../context/userContext";
import { FALLBACK_THUMB, GEMINI_URL, fallbackTo, readTime, stripHtml, uploadUrl } from "../constants";

const PostDetail = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState("");
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const { currentUser } = useContext(UserContext);

  useEffect(() => {
    const getPost = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/posts/${id}`);
        setPost(response.data);
      } catch (err) {
        setError(err.response?.data?.message || "This post couldn't be loaded.");
      }
      setIsLoading(false);
    };
    getPost();
  }, [id]);

  const generateSummary = async () => {
    if (!post) return;
    setIsSummaryLoading(true);
    try {
      const response = await axios.post(GEMINI_URL, {
        contents: [{ parts: [{ text: "summarise this: " + stripHtml(post.description) }] }],
      });
      setSummary(response.data.candidates[0].content.parts[0].text);
    } catch {
      setSummary("Couldn't generate a summary right now. Try again in a moment.");
    }
    setIsSummaryLoading(false);
  };

  if (isLoading) return <Loader label="Loading post…" />;

  if (error || !post) {
    return (
      <div className="container article">
        <div className="empty">
          <h3>Post not available</h3>
          <p>{error}</p>
          <Link to="/" className="btn">
            Back to feed
          </Link>
        </div>
      </div>
    );
  }

  return (
    <article className="container article">
      <Link to="/" className="article__back">
        <FiArrowLeft /> All posts
      </Link>

      <div className="reveal" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Link className="chip" to={`/posts/categories/${post.category}`}>
          {post.category}
        </Link>
        <span className="eyebrow">{readTime(post.description)}</span>
      </div>

      <h1 className="article__title">
        <span className="mask">
          <span>{post.title}</span>
        </span>
      </h1>

      <div className="article__meta">
        <PostAuthor authorID={post.creator} createdAt={post.createdAt} />
        {currentUser?.id === post.creator && (
          <div className="article__actions">
            <Link to={`/posts/${post._id}/edit`} className="btn btn--sm">
              <FiEdit2 /> Edit
            </Link>
            <DeletePost postId={id} />
          </div>
        )}
      </div>

      <div className="article__media reveal">
        <img src={uploadUrl(post.thumbnail)} alt="" onError={fallbackTo(FALLBACK_THUMB)} />
      </div>

      <div className="prose" dangerouslySetInnerHTML={{ __html: post.description }} />

      <section className="ai-panel">
        <div className="ai-panel__head">
          <h3>
            <FiStar /> TL;DR by Gemini
          </h3>
          <button
            className="btn btn--accent btn--sm"
            onClick={generateSummary}
            disabled={isSummaryLoading}
          >
            {summary ? "Regenerate" : "Summarise this post"}
          </button>
        </div>
        <div className="ai-panel__body">
          {isSummaryLoading ? (
            <Loader inline label="Reading the post…" />
          ) : (
            summary || <span className="muted">Short on time? Get the key points in a few lines.</span>
          )}
        </div>
      </section>
    </article>
  );
};

export default PostDetail;
